
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import {
    ArchiveBoxIcon,
    Bars3Icon,
    CalendarIcon,
    ChartPieIcon,
    Cog6ToothIcon,
    DocumentDuplicateIcon,
    LinkIcon,
    FolderIcon,
    HomeIcon,
    MegaphoneIcon,
    Squares2X2Icon,
    UserGroupIcon,
    UsersIcon,
    WrenchScrewdriverIcon,
    XMarkIcon,
    ClockIcon,
    QuestionMarkCircleIcon,
    CalendarDaysIcon,
    ArrowPathIcon,
    LifebuoyIcon,
} from '@heroicons/react/24/outline';
import {
    Link,
    Outlet,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getBusinessId, setBusinessId } from '../../lib/Api';
import Loading from '../../components/Loading';
import { ErrorContext } from '../../lib/ErrorContext';
import RequiresAuth from '../../components/RequiresAuth.jsx';
import { useBusinessInfo, useUserInfoQuery } from '../../lib/Queries';
import { ToastContainer } from 'react-toastify';
import BusinessSwitcher from '../../components/BusinessSwitcher.jsx';
import DropdownButton from '../../components/DropdownButton.jsx';
import LogoutIcon from '../../components/icons/LogoutIcon.jsx';
import ReportIcon from '../../components/icons/ReportIcon';
import { useSetCurrency } from '../../lib/CurrencyHelpers.js';
import SetupSteps from '../../components/onboarding/SetupSteps.jsx';
import TestModeToggle from '../../components/TestModeToggle.jsx';
import { formatDateLong } from '../../lib/DateTime.js';
import useBusinessNavigate from '../../lib/useBusinessNavigate.js';
import TrialBanner from '../../components/TrialBanner.jsx';
import * as Toast from '../../lib/Toast.js';
import * as Sentry from '@sentry/react';
import ErrorPage from '../ErrorPage.jsx';
import { PlusCircleIcon } from '@heroicons/react/24/outline/index';
import CertificateIcon from '~/components/icons/CertificateIcon';
import {
    FEATURE_COUPONS,
    FEATURE_GIFT_CERTIFICATES,
    FEATURE_INVOICES,
    FEATURE_MARKETING,
    FEATURE_PACKAGES,
    FEATURE_SUBSCRIPTIONS,
    FEATURE_WAITLIST,
    FEATURE_CUSTOM_QUESTIONS,
    useFeatures
} from '~/lib/Features';
import BringToFrontIcon from '~/components/icons/BringToFrontIcon';
import usePermissions from '~/lib/usePermissions';
import ClipboardTextIcon from '~/components/icons/ClipboardTextIcon';
import GiftIcon from '~/components/icons/GiftIcon';
import InvoiceIcon from '~/components/icons/InvoiceIcon';
import AssistantChatbox from '~/components/assistant/AssistantChatbox.jsx';
import { useSetLocale } from '~/lib/useSetLocale.js';
import Intercom from '@intercom/messenger-js-sdk';
import Modal from '~/components/Modal';
import AssistantFormEdit from '~/components/assistant/AssistantFormEdit.jsx';
import { trackAmplitudeEvent } from '~/lib/AmplitudeTracking';
import { isMobile } from '~/lib/Utils';
import useIsAssistantEnabled from '~/lib/useIsAssistantEnabled.js';
import { LightBulbIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { useInsightsQuery } from '~/lib/Queries';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function Layout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { pathname } = useLocation();
    const { businessId } = useParams();
    const [errors, setErrors] = useState({});
    const [errorsUpdatedAt, setErrorsUpdatedAt] = useState();
    const navigate = useNavigate();
    setBusinessId(businessId);
    const businessNavigate = useBusinessNavigate();
    const { data: userData } = useUserInfoQuery();
    const chatboxRef = useRef();

    const [isSetupStepsVisible, setIsSetupStepsVisible] = useState(false);
    const [isTestModeVisible, setIsTestModeVisible] = useState(false);

    const [outletKey, setOutletKey] = useState('1');
    const [isAssistantFormEditModalOpen, setIsAssistantFormEditModalOpen] = useState(false);

    const { data } = useBusinessInfo(() => {
        setBusinessId(null);
        setTimeout(() => {
            Toast.error('Unable to access business, please try again');
            navigate('/');
        }, 300);
    });
    const { isEnabled } = useFeatures();
    const { can, isFullAccess } = usePermissions();
    const { data: insightsData } = useInsightsQuery();
    const insightsCount = insightsData?.insights?.length || 0;

    const { business } = data || {};

    const isAssistantEnabled =
        useIsAssistantEnabled() &&
        !pathname.includes('/assistant/forms')
    ;

    useSetLocale(business);

    function openAiChat(isNewBusiness) {
        const isDesktop = !isMobile();

        // A/B test: For desktop users with new business, 50% get AssistantFormEdit
        // if (isNewBusiness && isDesktop) {
        //     const showFormEdit = Math.random() < 0.2;
        //
        //     // Track the A/B test event
        //     trackAmplitudeEvent('Assistant Onboarding Test', {
        //         isFormEdit: showFormEdit
        //     });
        //
        //     if (showFormEdit) {
        //         // Show AssistantFormEdit modal
        //         setIsAssistantFormEditModalOpen(true);
        //         return;
        //     }
        // }

        // Default behavior: open regular chat
        // Try every 250ms for the next 5 seconds
        const maxAttempts = 20;
        let attempts = 0;
        const interval = setInterval(() => {
            if (attempts >= maxAttempts) {
                clearInterval(interval);
                return;
            }
            if (chatboxRef.current) {
                clearInterval(interval);
                chatboxRef.current.openChat(isNewBusiness);
            } else {
                attempts++;
            }
        }, 250);
    }

    useEffect(() => {
        if (!userData) {
            return;
        }

        Intercom({
            app_id: 'rw2ybq6d',
            user_id: userData.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
            name: userData.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
            email: userData.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
            created_at: userData.created_at, // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
            hide_default_launcher: true,
        });
    }, [userData]);

    useEffect(() => {
        // Reset errors on navigate
        setErrors({});
        setSidebarOpen(false);
        window.scrollTo(0, 0);
    }, [pathname]);

    const businessPrefix = '/business/' + getBusinessId();
    const basePathname = pathname.replace(businessPrefix, '');
    const navigation = [
        {
            name: 'Calendar',
            href: `${businessPrefix}/calendar`,
            icon: CalendarDaysIcon,
            current: basePathname === '/calendar',
        },
        can('clients') && {
            name: 'Clients',
            href: `${businessPrefix}/clients`,
            icon: UserGroupIcon,
            current: basePathname === '/clients',
        },
        can('forms') && {
            name: 'Forms',
            href: `${businessPrefix}/forms`,
            icon: DocumentDuplicateIcon,
            current:
                basePathname.startsWith('/forms') ||
                basePathname === '/scheduling/pages',
        },
        isEnabled(FEATURE_CUSTOM_QUESTIONS) &&
            isFullAccess() && {
                name: 'Extensions',
                href: `${businessPrefix}/custom-questions`,
                icon: PuzzlePieceIcon,
                current: basePathname.startsWith('/custom-questions'),
            },
        can('reports') && {
            name: 'Reports',
            href: `${businessPrefix}/reports`,
            icon: ReportIcon,
            current: basePathname.startsWith('/reports'),
        },
        (can('availability') || can('eventTypes')) && {
            name: 'Scheduling',
            href: `${businessPrefix}/scheduling/availability`,
            icon: null,
            current: basePathname === '/scheduling',
        },
        can('forms') && {
            name: 'Booking Links',
            href: `${businessPrefix}/booking-links`,
            icon: LinkIcon,
            current: basePathname === '/booking-links',
        },
        can('availability') && {
            name: 'Availability',
            href: `${businessPrefix}/scheduling/availability`,
            icon: ClockIcon,
            current: basePathname === '/scheduling/availability',
        },
        can('eventTypes') && {
            name: 'Event Types',
            href: `${businessPrefix}/scheduling/event-types`,
            icon: CalendarIcon,
            current: basePathname.startsWith('/scheduling/event-types'),
        },
        isEnabled(FEATURE_WAITLIST) &&
            can('waitlist') && {
                name: 'Waitlisting',
                href: `${businessPrefix}/waitlist`,
                icon: ClipboardTextIcon,
                current: basePathname.startsWith('/waitlist'),
            },
        (isEnabled(FEATURE_PACKAGES) ||
            isEnabled(FEATURE_SUBSCRIPTIONS) ||
            isEnabled(FEATURE_COUPONS) ||
            isEnabled(FEATURE_GIFT_CERTIFICATES)) &&
            (can('packages') || can('coupons')) && {
                name: 'Selling',
                href: `${businessPrefix}/packages`,
                icon: null,
                current: false,
            },
        can('packages') &&
            isEnabled(FEATURE_PACKAGES) && {
                name: 'Packages',
                href: `${businessPrefix}/packages`,
                icon: ArchiveBoxIcon,
                current: basePathname.startsWith('/packages'),
            },
        can('packages') &&
            isEnabled(FEATURE_SUBSCRIPTIONS) && {
                name: 'Subscriptions',
                href: `${businessPrefix}/subscriptions`,
                icon: ArrowPathIcon,
                current: basePathname.startsWith('/subscriptions'),
            },
        can('coupons') &&
            isEnabled(FEATURE_COUPONS) && {
                name: 'Coupons',
                href: `${businessPrefix}/coupons`,
                icon: CertificateIcon,
                current: basePathname.startsWith('/coupons'),
            },
        can('giftcertificates') &&
            isEnabled(FEATURE_GIFT_CERTIFICATES) && {
                name: 'Gift Certificates',
                href: `${businessPrefix}/gift-certificates`,
                icon: GiftIcon,
                current: basePathname.startsWith('/gift-certificates'),
            },
        can('invoice') &&
            isEnabled(FEATURE_INVOICES) && {
                name: 'Invoices',
                href: `${businessPrefix}/invoices`,
                icon: InvoiceIcon,
                current: basePathname.startsWith('/invoices'),
            },
        (
            (isEnabled(FEATURE_MARKETING) && can('marketing'))
            || (can('insights') && isAssistantEnabled))
        &&
        (can('packages') || can('coupons')) && {
            name: 'Marketing',
            href: `${businessPrefix}/insights`,
            icon: null,
            current: false,
        },
        isEnabled(FEATURE_MARKETING) && can('marketing') && {
            name: 'Automations',
            href: `${businessPrefix}/marketing`,
            icon: MegaphoneIcon,
            current: basePathname.startsWith('/marketing'),
        },
        can('insights') && isAssistantEnabled && {
            name: 'Insights',
            href: `${businessPrefix}/insights`,
            icon: LightBulbIcon,
            current: basePathname.startsWith('/insights'),
            badge: insightsCount > 0 ? insightsCount : null,
        },
    ].filter(Boolean);

    function navItemClasses(item, idx) {
        return classNames(
            !!item.icon &&
                (item.current
                    ? 'text-primary-700'
                    : 'text-gray-700 hover:text-primary-700'),
            item.icon && idx > 0 && '',
            'group no-underline flex gap-x-3 p-1 rounded-md text-base items-center',
            !item.icon &&
                'ml-8 uppercase pb-0 pt-4 text-gray-500 text-xs font-semibold',
        );
    }

    return (
        <Sentry.ErrorBoundary
            fallback={({ eventId }) => <ErrorPage eventId={eventId} />}
        >
            <RequiresAuth>
                <>
                    {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-white">
        <body class="h-full">
        ```
      */}
                    {/* Straight under <body>, outside the app, so an open
                        Headless UI Dialog (which makes the app inert) doesn't
                        stop toasts being clicked or hovered */}
                    {createPortal(
                        <ToastContainer
                            position="top-center"
                            autoClose={5000}
                            hideProgressBar
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="light"
                        />,
                        document.body,
                    )}
                    <div>
                        <Transition show={sidebarOpen}>
                            <Dialog
                                className="relative z-50 xl:hidden"
                                onClose={setSidebarOpen}
                            >
                                <TransitionChild
                                    enter="transition-opacity ease-linear duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="transition-opacity ease-linear duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="fixed inset-0 bg-gray-900/80" />
                                </TransitionChild>

                                <div className="fixed inset-0 flex">
                                    <TransitionChild
                                        enter="transition ease-in-out duration-300 transform"
                                        enterFrom="-translate-x-full"
                                        enterTo="translate-x-0"
                                        leave="transition ease-in-out duration-300 transform"
                                        leaveFrom="translate-x-0"
                                        leaveTo="-translate-x-full"
                                    >
                                        <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                                            <TransitionChild
                                                enter="ease-in-out duration-300"
                                                enterFrom="opacity-0"
                                                enterTo="opacity-100"
                                                leave="ease-in-out duration-300"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                            >
                                                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                                    <button
                                                        type="button"
                                                        className="-m-2.5 p-2.5"
                                                        onClick={() =>
                                                            setSidebarOpen(
                                                                false,
                                                            )
                                                        }
                                                    >
                                                        <span className="sr-only">
                                                            Close sidebar
                                                        </span>
                                                        <XMarkIcon
                                                            className="h-6 w-6 text-white"
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </div>
                                            </TransitionChild>
                                            {/* Sidebar component, swap this element with another sidebar if you like */}
                                            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                                                <nav className="flex flex-1 flex-col">
                                                    <div
                                                        role="list"
                                                        className="flex flex-1 flex-col gap-y-7"
                                                    >
                                                        <div>
                                                            <div
                                                                role="list"
                                                                className="-mx-2 space-y-1 pt-4"
                                                            >
                                                                <div className="pl-[6px] pb-4 xs:hidden">
                                                                    {/* Mobile Sidebar Slideout */}
                                                                    <BusinessSwitcher
                                                                        currentBusiness={business}
                                                                    />
                                                                </div>

                                                                {navigation.map(
                                                                    (
                                                                        item,
                                                                        idx,
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                item.name
                                                                            }
                                                                        >
                                                                            <Link
                                                                                to={
                                                                                    item.href
                                                                                }
                                                                                className={navItemClasses(
                                                                                    item,
                                                                                    idx,
                                                                                )}
                                                                            >
                                                                                {item.icon && (
                                                                                    <item.icon
                                                                                        className={classNames(
                                                                                            'h-5 w-5 shrink-0',
                                                                                        )}
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                )}

                                                                                {
                                                                                    item.name
                                                                                }
                                                                                {item.badge && (
                                                                                    <span className="ml-auto bg-primary-700 text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                                                                                        {item.badge}
                                                                                    </span>
                                                                                )}
                                                                            </Link>
                                                                        </div>
                                                                    ),
                                                                )}

                                                                {can(
                                                                    'integrations',
                                                                ) && (
                                                                    <Link
                                                                        to={`/business/${businessId}/integrations`}
                                                                        className={classNames(
                                                                            basePathname ===
                                                                                '/integrations'
                                                                                ? 'text-primary-700'
                                                                                : 'text-gray-700 hover:text-primary-700',
                                                                            'mt-1',
                                                                            'group no-underline flex gap-x-3 p-1 pt-8 rounded-md text-base items-center',
                                                                        )}
                                                                    >
                                                                        <BringToFrontIcon
                                                                            className={classNames(
                                                                                'h-5 w-5 shrink-0 -ml-1',
                                                                            )}
                                                                            aria-hidden="true"
                                                                        />
                                                                        Features
                                                                        &amp;
                                                                        Integrations
                                                                    </Link>
                                                                )}
                                                                <a
                                                                    href="https://support.breely.com/"
                                                                    target="_blank"
                                                                    className={classNames(
                                                                        'text-gray-700 hover:text-primary-700',
                                                                        'mt-1',
                                                                        'group no-underline flex gap-x-3 p-1 rounded-md text-base items-center',
                                                                    )}
                                                                >
                                                                    <QuestionMarkCircleIcon
                                                                        className={classNames(
                                                                            'h-5 w-5 shrink-0 -ml-1',
                                                                        )}
                                                                        aria-hidden="true"
                                                                    />
                                                                    Support
                                                                </a>
                                                                <Link
                                                                    to={`/business/${businessId}/settings`}
                                                                    className={classNames(
                                                                        basePathname ===
                                                                            'settings'
                                                                            ? 'text-primary-700'
                                                                            : 'text-gray-700 hover:text-primary-700',
                                                                        'group flex gap-x-3 items-center rounded-md p-1 text-base leading-6 no-underline',
                                                                    )}
                                                                >
                                                                    <Cog6ToothIcon
                                                                        className={classNames(
                                                                            'h-5 w-5 shrink-0 -ml-1',
                                                                        )}
                                                                        aria-hidden="true"
                                                                    />
                                                                    Settings
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </nav>
                                            </div>
                                        </DialogPanel>
                                    </TransitionChild>
                                </div>
                            </Dialog>
                        </Transition>

                        {/* Static sidebar for desktop */}
                        <div className="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col">
                            {/* Sidebar component, swap this element with another sidebar if you like */}
                            <div className="flex grow w-full flex-col gap-y-5 overflow-y-auto overflow-x-hidden bg-white px-6">
                                <div className="flex h-16 shrink-0 items-center border-b lg:-ml-6 lg:-mr-6 lg:pl-6">
                                    {/* Desktop Sidebar */}
                                    <BusinessSwitcher
                                        currentBusiness={business}
                                    />
                                </div>

                                <TestModeToggle />
                                <nav className="flex flex-1 flex-col">
                                    <div
                                        role="list"
                                        className="flex flex-1 flex-col gap-y-7"
                                    >
                                        <div>
                                            <div
                                                role="list"
                                                className="-mx-2 space-y-1"
                                            >
                                                {navigation.map((item, idx) => (
                                                    <div key={item.name}>
                                                        <Link
                                                            to={item.href}
                                                            className={navItemClasses(
                                                                item,
                                                                idx,
                                                            )}
                                                        >
                                                            {!!item.icon && (
                                                                <item.icon
                                                                    className={classNames(
                                                                        'h-5 w-5 shrink-0',
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                            )}

                                                            {item.name}
                                                            {item.badge && (
                                                                <span className=" bg-primary-700 text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                                                                    {item.badge}
                                                                </span>
                                                            )}
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mt-auto pb-6 -mx-1">
                                            <SetupSteps openAiChat={openAiChat} className="mb-6" />

                                            {can('integrations') && (
                                                <Link
                                                    to={`/business/${businessId}/integrations`}
                                                    className={classNames(
                                                        basePathname ===
                                                            '/integrations'
                                                            ? 'text-primary-700'
                                                            : 'text-gray-700 hover:text-primary-700',
                                                        'mt-1',
                                                        'group no-underline flex gap-x-3 p-1 rounded-md text-base items-center',
                                                    )}
                                                >
                                                    <BringToFrontIcon
                                                        className={classNames(
                                                            'h-5 w-5 shrink-0 -ml-1',
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                    Features &amp; Integrations
                                                </Link>
                                            )}
                                            {/*<a*/}
                                            {/*    href="https://support.breely.com/"*/}
                                            {/*    target="_blank"*/}
                                            {/*    className={classNames(*/}
                                            {/*        'text-gray-700 hover:text-primary-700',*/}
                                            {/*        'mt-1',*/}
                                            {/*        'group no-underline flex gap-x-3 p-1 rounded-md text-base items-center',*/}
                                            {/*    )}*/}
                                            {/*>*/}
                                            {/*    <QuestionMarkCircleIcon*/}
                                            {/*        className={classNames(*/}
                                            {/*            'h-5 w-5 shrink-0 -ml-1',*/}
                                            {/*        )}*/}
                                            {/*        aria-hidden="true"*/}
                                            {/*    />*/}
                                            {/*    Support*/}
                                            {/*</a>*/}
                                            <Link
                                                to={`/business/${businessId}/settings`}
                                                className={classNames(
                                                    basePathname === '/settings'
                                                        ? 'text-primary-700'
                                                        : 'text-gray-700 hover:text-primary-700',
                                                    'mt-1',
                                                    'group no-underline flex gap-x-3 p-1 rounded-md text-base items-center',
                                                )}
                                            >
                                                <Cog6ToothIcon
                                                    className={classNames(
                                                        'h-5 w-5 shrink-0 -ml-1',
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                Settings
                                            </Link>
                                        </div>
                                    </div>
                                </nav>
                            </div>
                        </div>

                        <div className="sticky top-0 z-10 flex flex-wrap sm:flex-nowrap items-center gap-x-2 bg-white px-2 py-2 shadow-sm sm:px-6 xl:hidden">
                                <button
                                    type="button"
                                    className="sm:p-2.5 text-gray-700 xl:hidden"
                                    onClick={() => setSidebarOpen(true)}
                                >
                                    <span className="sr-only">Open sidebar</span>
                                    <Bars3Icon
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                             <div className="hidden xs:flex items-center">
                                {/* Mobile Top */}
                                 {(isSetupStepsVisible || isTestModeVisible) && (
                                     <BusinessSwitcher
                                         businessNameClassName="xs:hidden sm:flex"
                                         currentBusiness={business}
                                     />
                                 )}
                            </div>

                            {!isSetupStepsVisible && !isTestModeVisible && (
                                <BusinessSwitcher
                                    businessNameClassName="xs:hidden sm:flex"
                                    currentBusiness={business}
                                />
                            )}

                            <SetupSteps
                                className="sm:w-auto order-last sm:order-none sm:ml-auto"
                                openAiChat={openAiChat}
                                setIsVisible={setIsSetupStepsVisible}
                            />
                            <TestModeToggle
                                className="ml-auto sm:ml-0 sm:w-auto"
                                setIsVisible={setIsTestModeVisible}
                            />
                        </div>

                        <main className="xl:pl-72">
                            <div
                                className={`px-4 sm:px-8 xl:px-10 ${!!business?.is_trial ? 'pb-12' : 'pb-8'}`}
                            >
                                <div className={`mt-4 grid gap-8 items-center md:gap-4 ${basePathname === '/calendar' ? 'grid-cols-[1fr_auto] md:grid-cols-[1fr_340px]' : 'grid-cols-[minmax(0,42rem)_auto]'}`}>
                                    {isAssistantEnabled && (
                                        <AssistantChatbox
                                            ref={chatboxRef}
                                            reload={() =>
                                                setOutletKey(new Date().getTime())
                                            }
                                            key={businessId}
                                        />
                                    )}
                                    <a
                                        href="https://support.breely.com"
                                        target="_blank"
                                        className={`text-gray-500 hover:text-gray-600 no-underline flex items-center justify-self-end`}
                                    >
                                        <LifebuoyIcon className="w-5 h-5 mr-1" />
                                        Support
                                    </a>
                                </div>

                                <ErrorContext.Provider
                                    value={{
                                        errors: errors,
                                        setErrors: setErrors,
                                        setErrorsUpdatedAt: setErrorsUpdatedAt,
                                        errorsUpdatedAt: errorsUpdatedAt,
                                    }}
                                >
                                    <Outlet key={outletKey} />
                                </ErrorContext.Provider>
                            </div>
                        </main>
                        <TrialBanner business={business} businessData={data} />
                    </div>

                    {/* A/B Test: AssistantFormEdit Modal */}
                    <Modal
                        isOpen={isAssistantFormEditModalOpen}
                        onClose={() => setIsAssistantFormEditModalOpen(false)}
                        widthClass="sm:max-w-[100%] sm:max-h-[80%] sm:my-2 sm:mx-2"
                        panelClassName="!bg-white !p-0 !pl-6"
                    >
                        <AssistantFormEdit />
                    </Modal>
                </>
            </RequiresAuth>
        </Sentry.ErrorBoundary>
    );
}
